import PrimaryInnerGlowButton, { PrimaryDefaultButton } from '@/components/buttons/primary';
import FrontPinkButton from '@/components/buttons/primary/FrontPinkButton';
import FilterBlock from '@/components/FilterBlock';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { GlobalContext } from '@/context';
import { CustomerStoriesHomepageQuery } from '@/graphql';
import CustomerStoriesHomepageQueryDocument from '@/graphql/queries/CustomerStoriesHomepage.gql';
import gtm from '@/lib/gtm';
import { getStaticPageProps } from '@/lib/next';
import { maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { marginX, mediaBreakpointUp, paddingX, paddingY } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ArrayParam, useQueryParam } from 'use-query-params';

const CUSTOMER_STORY_CARD_COVER_VERTICAL_FOCUS_MULTIPLIER = 2.15;

// #region - Components

const PageWrapper = styled.div`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

const Hero = styled.header`
  background-color: ${COLORS.VIOLET.HIGHLIGHT};
  padding: ${remCalc(16)};
  border-radius: ${remCalc(10)};

  margin-left: calc(var(--breakpoint-padding-x) * -0.5);
  margin-right: calc(var(--breakpoint-padding-x) * -0.5);
  margin-bottom: ${remCalc(24)};

  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;

  row-gap: ${remCalc(24)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: 1fr minmax(0, ${remCalc(300)});
    grid-template-rows: auto;
    align-items: center;

    column-gap: ${remCalc(16)};

    ${marginX(0)}
  }

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(60)};
    grid-template-columns: 3fr 2fr;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const HeroLeft = styled.div`
  ${paddingX(16)}
  ${paddingY(24)}

  ${mediaBreakpointUp('desktop')} {
    ${paddingX(84)}
    ${paddingY(48)}
  }

  ${PrimaryInnerGlowButton} {
    margin-top: ${remCalc(16)};
  }
`;

const HeroCustomerLogo = styled.div`
  margin-bottom: ${remCalc(8)};

  img {
    width: auto;
    max-height: 24px;
  }
`;

const HeroTitle = styled.p`
  ${customTextSize(40, 42)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: ${remCalc(16)};

  small {
    display: block;
    ${customTextSize(16, 26)}
    font-weight: ${FONT_WEIGHTS.NORMAL};
    margin-bottom: ${remCalc(8)};
  }
`;

const HeroRight = styled.div`
  background-color: ${COLORS.WHITE};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(16)};
`;

const HeroImage = styled(Image)`
  border-radius: ${remCalc(10)};
  overflow: hidden;
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  grid-gap: ${remCalc(32)};

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: ${remCalc(288)} auto;
    grid-template-rows: auto;
  }
`;

const StyledFilterBlock = styled(FilterBlock)`
  position: sticky;
  top: ${remCalc(PRIMARY_NAV_HEIGHT + 20)};
`;

const ResourceCardGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-auto-rows: auto;
  column-gap: ${remCalc(16)};
  row-gap: ${remCalc(32)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ResourceCardImage = styled.div`
  position: relative;
  height: ${remCalc(200)};
  border-radius: ${remCalc(10)};
  overflow: hidden;
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  transition: border-radius 0.15s ease-in-out;
`;

/*
 * We make the resource card image taller than the div that contains it. The parent div has overflow: hidden, so only the top band of the image is visible.
 * This is done this way due to a Design team ask, where we crop the original hero image from a customer story to focus on the brand logo.
 * All hero images should follow the same format and are cropped to the same height, so we can use a fixed multiplier to calculate the height of the inner div.
 */
const ResourceCardImageInner = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  height: ${CUSTOMER_STORY_CARD_COVER_VERTICAL_FOCUS_MULTIPLIER * 100}%;

  img {
    object-fit: cover;
    object-position: top center;
  }
`;

const ResourceCardContent = styled.div`
  padding: ${remCalc(16, 8)};

  h3 {
    ${customTextSize(24, 30)}
    font-weight: ${FONT_WEIGHTS.MEDIUM};
    margin-bottom: ${remCalc(8)};

    small {
      display: block;
      ${customTextSize(14, 24)}
      font-weight: ${FONT_WEIGHTS.NORMAL};
      color: ${COLORS.GRAY.BASE};
    }
  }
`;

const ResourceCard = styled.div`
  width: 100%;
  background-color: transparent;
  border-radius: ${remCalc(10)};
  max-width: ${remCalc(288)};
  margin: 0 auto;
  transition: background-color 0.15s ease-in-out;

  ${mediaBreakpointUp('tablet')} {
    max-width: none;
  }

  &:hover {
    background-color: ${COLORS.GRAY.HIGHLIGHT};

    ${ResourceCardImage} {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  & > a {
    display: block;
  }
`;

const StyledPrimaryDefaultButton = styled(PrimaryDefaultButton)`
  --button-color: ${COLORS.BLUE.LOWLIGHT};
  --button-text-color: ${COLORS.WHITE};
  --button-color-hover: ${COLORS.BLACK};
  --button-text-color-hover: ${COLORS.WHITE};

  width: 100%;

  ${mediaBreakpointUp('tablet')} {
    width: auto;
  }
`;

const LargeCalloutBlock = styled.div`
  padding: ${remCalc(32)};
  margin-bottom: ${remCalc(40)};
  margin-top: ${remCalc(40)};

  border-radius: ${remCalc(10)};

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${remCalc(60)};

  ${mediaBreakpointUp('tablet')} {
    padding: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(100)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${paddingX(40)};
    ${paddingY(70)};
  }

  p {
    ${customTextSize(40, 42)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLACK};
    text-align: center;
    max-width: ${remCalc(1100)};
    margin: 0 auto;

    ${mediaBreakpointUp('desktopWide')} {
      ${customTextSize(44, 42)};
    }
  }

  ${StyledPrimaryDefaultButton} {
    --button-color: ${COLORS.WHITE};
    --button-text-color: ${COLORS.BLACK};
    --button-color-hover: ${COLORS.BLACK};
    --button-text-color-hover: ${COLORS.WHITE};
  }
`;

const ShowAllWrapper = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: ${remCalc(24, 0)};

  button {
    color: ${COLORS.BLACK};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }
`;

const CTASection = styled.div`
  ${paddingY(40)};
`;

// #endregion - Components

interface CustomerStoriesHomepageProps extends CustomerStoriesHomepageQuery { }

const CustomerStoriesHomepage: PageTemplate<CustomerStoriesHomepageProps> = ({ entry, entries }) => {
  const { toggleGetStartedModal } = useContext(GlobalContext);
  const [selectedTeams, setSelectedTeams] = useQueryParam('team', ArrayParam);
  const [selectedTopics, setSelectedTopics] = useQueryParam('topic', ArrayParam);
  const [selectedIndustries, setSelectedIndustries] = useQueryParam('industry', ArrayParam);
  const [showAll, setShowAll] = useState(false);

  const hasFiltersApplied = Boolean(selectedTeams?.length || selectedTopics?.length || selectedIndustries?.length);

  const teams = useMemo(() => {
    const teams =
      entries
        ?.flatMap((entry) => {
          return entry?.customer?.[0]?.teams;
        })
        .filter(notEmpty) ?? [];

    if (!teams.length) return [];

    return teams
      .reduce((all, team) => {
        if (!all.some(({ id }) => id === team.id)) {
          return [...all, team];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const topics = useMemo(() => {
    const topics =
      entries
        ?.flatMap((entry) => {
          return entry?.topics;
        })
        .filter(notEmpty) ?? [];

    if (!topics.length) return [];

    return topics
      .reduce((all, topic) => {
        if (!all.some(({ id }) => id === topic.id)) {
          return [...all, topic];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const industries = useMemo(() => {
    const industries =
      entries
        ?.flatMap((entry) => {
          return entry?.customer?.[0]?.industries;
        })
        .filter(notEmpty) ?? [];

    if (!industries.length) return [];

    return industries
      .reduce((all, industry) => {
        if (!all.some(({ id }) => id === industry.id)) {
          return [...all, industry];
        }

        return all;
      }, [])
      .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''));
  }, [entries]);

  const filteredStories = useMemo(() => {
    if (!entries) return null;

    const stories = [...entries.filter(notEmpty)];

    if (!hasFiltersApplied) return stories;

    return stories
      .filter((story) =>
        selectedTeams?.length
          ? selectedTeams.some((slug) => story.customer?.[0]?.teams?.some((team) => team?.slug === slug))
          : true,
      )
      .filter((story) =>
        selectedTopics?.length
          ? selectedTopics.some((slug) => story.topics?.some((topic) => topic?.slug === slug))
          : true,
      )
      .filter((story) =>
        selectedIndustries?.length
          ? selectedIndustries.some((slug) =>
            story.customer?.[0]?.industries?.some((industry) => industry?.slug === slug),
          )
          : true,
      );
  }, [entries, selectedTeams, selectedTopics, selectedIndustries]);

  const filterSelections = useMemo(() => {
    let filterIds: string[] = [];

    if (selectedTeams?.length) {
      selectedTeams.filter(notEmpty).forEach((slug) => {
        const match = teams.find((team) => team.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    if (selectedTopics?.length) {
      selectedTopics.filter(notEmpty).forEach((slug) => {
        const match = topics.find((topic) => topic.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    if (selectedIndustries?.length) {
      selectedIndustries.filter(notEmpty).forEach((slug) => {
        const match = industries.find((industry) => industry.slug === slug);

        if (match) {
          filterIds.push(match.id!);
        }
      });
    }

    return filterIds;
  }, [selectedTeams, teams, selectedTopics, topics, selectedIndustries, industries]);

  const featuredCustomerStory = useMemo(() => {
    if (!entries) return null;

    return entries.filter(notEmpty).find(({ id }) => id === entry?.featuredCustomerStory?.[0]?.id);
  }, [entry?.featuredCustomerStory, entries]);

  const isFiltered = hasFiltersApplied || showAll;

  return (
    <PageWrapper>
      <Container>
        {featuredCustomerStory && (
          <Hero>
            <HeroLeft>
              <HeroCustomerLogo>
                <Image
                  src={featuredCustomerStory.customer?.[0]?.customerLogo?.[0]?.url!}
                  width={featuredCustomerStory.customer?.[0]?.customerLogo?.[0]?.width!}
                  height={featuredCustomerStory.customer?.[0]?.customerLogo?.[0]?.height!}
                  alt={`${featuredCustomerStory.customer?.[0]?.title} logo'`}
                />
              </HeroCustomerLogo>

              <HeroTitle>{featuredCustomerStory.heroTitle ?? ''}</HeroTitle>

              <Link href={featuredCustomerStory.url ?? '#'}>
                <PrimaryInnerGlowButton
                  as="a"
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      location: 'Hero',
                      element_label: 'Read customer story',
                      customer_story_title: maybeNotSet(featuredCustomerStory.title),
                    });
                  }}
                >
                  Read customer story
                </PrimaryInnerGlowButton>
              </Link>
            </HeroLeft>

            <HeroRight>
              {featuredCustomerStory.coverImage?.length && (
                <HeroImage
                  src={featuredCustomerStory.coverImage?.[0]?.url!}
                  width={featuredCustomerStory.coverImage?.[0]?.width!}
                  height={featuredCustomerStory.coverImage?.[0]?.height!}
                  alt=""
                />
              )}
            </HeroRight>
          </Hero>
        )}

        <Grid>
          <div>
            <StyledFilterBlock
              categories={[
                {
                  title: 'Teams',
                  slug: 'teams',
                  filters: teams.map((team) => ({
                    id: team.id!,
                    title: team.title!,
                    slug: team.slug!,
                  })),
                },
                {
                  title: 'Topics',
                  slug: 'topics',
                  filters: topics.map((topic) => ({
                    id: topic.id!,
                    title: topic.title!,
                    slug: topic.slug!,
                  })),
                },
                {
                  title: 'Industries',
                  slug: 'industries',
                  filters: industries.map((industry) => ({
                    id: industry.id!,
                    title: industry.title!,
                    slug: industry.slug!,
                  })),
                },
              ]}
              collapsible={false}
              selections={filterSelections}
              onFilterToggle={(category, filter, selected) => {
                switch (category.slug) {
                  case 'teams':
                    if (selected) {
                      setSelectedTeams([...(selectedTeams ?? []), filter.slug]);
                    } else {
                      setSelectedTeams(selectedTeams?.filter((team) => team !== filter.slug));
                    }

                    break;

                  case 'topics':
                    if (selected) {
                      setSelectedTopics([...(selectedTopics ?? []), filter.slug]);
                    } else {
                      setSelectedTopics(selectedTopics?.filter((topic) => topic !== filter.slug));
                    }

                    break;

                  case 'industries':
                    if (selected) {
                      setSelectedIndustries([...(selectedIndustries ?? []), filter.slug]);
                    } else {
                      setSelectedIndustries(selectedIndustries?.filter((industry) => industry !== filter.slug));
                    }

                    break;
                }

                gtm.track(selected ? 'checked_filter' : 'unchecked_filter', {
                  location: 'Customer Stories Homepage',
                  category_title: maybeNotSet(category.title),
                  filter_title: maybeNotSet(filter.title),
                });
              }}
            />
          </div>

          <ResourceCardGrid>
            {filteredStories?.slice(0, isFiltered ? undefined : 12).map((resource) => {
              const customer = resource.customer?.[0];
              const customerLogo = customer?.customerLogo?.[0];

              return (
                <ResourceCard key={resource.id}>
                  <Link href={resource.url ?? '#'}>
                    <a
                      onClick={() => {
                        gtm.track('click', {
                          location: 'Customer Stories Homepage',
                          element_type: 'link',
                          element_label: 'Card',
                          card_title: maybeNotSet(resource.title),
                        });
                      }}
                    >
                      <ResourceCardImage>
                        <ResourceCardImageInner>
                          <Image
                            src={resource.coverImage?.[0]?.url!}
                            alt=""
                            fill
                          />
                        </ResourceCardImageInner>
                      </ResourceCardImage>

                      <ResourceCardContent>
                        <h3>{resource.heroTitle}</h3>
                      </ResourceCardContent>
                    </a>
                  </Link>
                </ResourceCard>
              );
            })}

            {!showAll && !hasFiltersApplied ? (
              <ShowAllWrapper>
                <button
                  type="button"
                  onClick={() => {
                    setShowAll(true);

                    gtm.track('click', {
                      element_label: 'Show more/all',
                      element_type: 'button',
                      location: 'Customer Stories Homepage',
                    });
                  }}
                >
                  Show more ({(entries ?? []).length - 12})
                </button>
              </ShowAllWrapper>
            ) : null}
          </ResourceCardGrid>
        </Grid>
      </Container>
      <CTASection>
        <Container>
          <LargeCalloutBlock>
            <p>Ready to build long-lasting customer relationships?</p>

            <FrontPinkButton
              onClick={() => {
                toggleGetStartedModal(true);

                gtm.track('click', {
                  element_label: 'Get Started',
                  element_type: 'button',
                  location: 'Customer Stories Homepage Footer',
                });
              }}
            >
              Get started
            </FrontPinkButton>
          </LargeCalloutBlock>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

CustomerStoriesHomepage.template = MasterTemplate;

export default CustomerStoriesHomepage;

export const getStaticProps = getStaticPageProps('customer-stories', async ({ client }) => {
  const props = await client.request<CustomerStoriesHomepageQuery>(CustomerStoriesHomepageQueryDocument);

  return { props };
});
